import { FC, MouseEvent, ReactNode } from "react";

export type PillTone = "green" | "blue" | "amber" | "purple" | "red" | "neutral";

const PILL_TONE: Record<PillTone, string> = {
	green: "text-tone-green-fg bg-tone-green-bg",
	blue: "text-tone-blue-fg bg-tone-blue-bg",
	amber: "text-tone-amber-fg bg-tone-amber-bg",
	purple: "text-tone-purple-fg bg-tone-purple-bg",
	red: "text-tone-red-fg bg-tone-red-bg",
	neutral: "text-tone-neutral-fg bg-tone-neutral-bg"
};

/** Soft status badge. Keep the label short - pills never wrap. */
export const Pill: FC<{ tone?: PillTone; children: ReactNode }> = ({ tone = "neutral", children }) => (
	<span
		className={`inline-block whitespace-nowrap rounded-full px-2.5 py-1 text-[13px] font-bold ${PILL_TONE[tone]}`}
	>
		{children}
	</span>
);

/** Two-line cell: a strong primary line over a muted secondary one. */
export const StackedCell: FC<{ primary: ReactNode; secondary?: ReactNode }> = ({ primary, secondary }) => (
	<>
		<span className="block font-bold text-ink">{primary}</span>
		{secondary !== undefined && secondary !== null && secondary !== "" && (
			<span className="mt-0.5 block text-[13px] text-muted">{secondary}</span>
		)}
	</>
);

export type GridActionTone = "primary" | "attention" | "quiet" | "danger";

const ACTION_TONE: Record<GridActionTone, string> = {
	primary: "text-white bg-brand-green border-transparent",
	attention: "text-white bg-brand-orange border-transparent",
	quiet: "text-body bg-surface border-line-strong",
	danger: "text-white bg-brand-red border-transparent"
};

const ACTION_BASE =
	"font-[inherit] text-[13px] font-bold border rounded-md whitespace-nowrap cursor-pointer " +
	"hover:brightness-[0.94] disabled:cursor-default disabled:text-faint disabled:bg-tone-neutral-bg " +
	"disabled:border-transparent disabled:brightness-100";

/**
 * Row-level button. Put it in a column marked `stopRowClick` so pressing it
 * never also triggers the row's own click handler.
 */
export const GridAction: FC<{
	tone?: GridActionTone;
	disabled?: boolean;
	/** Square, borderless variant for icon-only actions such as an edit pencil. */
	icon?: boolean;
	title?: string;
	onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
	children: ReactNode;
}> = ({ tone = "quiet", disabled = false, icon = false, title, onClick, children }) => (
	<button
		type="button"
		title={title}
		aria-label={icon && typeof title === "string" ? title : undefined}
		disabled={disabled}
		onClick={onClick}
		className={[
			ACTION_BASE,
			icon
				? "inline-grid place-items-center h-8 w-8 p-0 text-muted bg-transparent border-transparent hover:bg-tone-neutral-bg hover:text-ink hover:brightness-100"
				: `min-w-[6.25rem] px-3.5 py-2 ${ACTION_TONE[tone]}`
		].join(" ")}
	>
		{children}
	</button>
);
